import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const emptyPiece = { weight: '', length_in: '', width_in: '', height_in: '' };

const emptyRow = {
  pro_number: '', destination_zip: '',
  is_residential: false, pickup_or_delivery: 'D',
  inside_charges: false, unpack_debris: false,
  liftgate_mode: 'auto', two_man_mode: 'auto',
  special_applied: false, special_time: 'normal',
  misc_charges: '', special_charges: '',
  misc_notes: '', special_notes: '',
  pieces: []
};

// ── Piece Entry Modal ──────────────────────────────────────────
const PieceModal = ({ row, customer, onConfirm, onClose }) => {
  const [pieces, setPieces] = useState(
    row.pieces && row.pieces.length > 0
      ? row.pieces
      : [{ ...emptyPiece, id: Date.now() }]
  );
  const [preview, setPreview] = useState(null);
  const previewTimer = React.useRef(null);

  const addPiece = () => setPieces(prev => [...prev, { ...emptyPiece, id: Date.now() }]);

  const removePiece = (id) => {
    if (pieces.length === 1) return toast.error('At least one piece is required');
    setPieces(prev => prev.filter(p => p.id !== id));
  };

  const updatePiece = (id, field, value) => {
    setPieces(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  // Live preview whenever pieces change
  useEffect(() => {
    clearTimeout(previewTimer.current);
    if (!row.destination_zip || row.destination_zip.length < 5) return;
    const validPieces = pieces.filter(p => p.weight);
    if (validPieces.length === 0) return;

    previewTimer.current = setTimeout(async () => {
      try {
        const res = await API.post('/invoices/preview', {
          customer_id: customer?.customer_id,
          destination_zip: row.destination_zip,
          is_residential: row.is_residential,
          inside_charges: row.inside_charges,
          unpack_debris: row.unpack_debris,
          liftgate_mode: row.liftgate_mode || 'auto',
          special_applied: row.special_applied || false,
          special_time: row.special_time || 'normal',
          two_man_mode: row.two_man_mode || 'auto',
          misc_charges: parseFloat(row.misc_charges) || 0,
          special_charges: parseFloat(row.special_charges) || 0,
          pieces: validPieces.map(p => ({
            weight: parseFloat(p.weight) || 0,
            length_in: parseFloat(p.length_in) || 0,
            width_in: parseFloat(p.width_in) || 0,
            height_in: parseFloat(p.height_in) || 0
          }))
        });
        setPreview(res.data);
      } catch {
        setPreview(null);
      }
    }, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces, row.destination_zip]);

  const totalActual = pieces.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0);

  const inputStyle = {
    width: '100%', padding: '6px 8px', border: '1px solid #E2E8F0',
    borderRadius: '4px', fontSize: '13px', outline: 'none'
  };

  const thStyle = {
    padding: '8px', background: '#1A2A3A', color: 'white',
    fontSize: '12px', fontWeight: '500', textAlign: 'left',
    borderRight: '1px solid #F5A800'
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="fcc-modal-pop" style={{ background: 'white', borderRadius: '12px', width: '700px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* Modal header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Piece Entry</h2>
            <p style={{ fontSize: '13px', color: '#5B6B7F', marginTop: '2px' }}>
              Pro #: <strong>{row.pro_number || '—'}</strong> · ZIP: <strong>{row.destination_zip || '—'}</strong>
            </p>
          </div>
          <button onClick={onClose} className="fcc-press" style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#5B6B7F' }}>✕</button>
        </div>

        {/* Piece table */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '40px' }}>#</th>
                <th style={thStyle}>Weight (lbs)</th>
                <th style={thStyle}>Length (in)</th>
                <th style={thStyle}>Width (in)</th>
                <th style={thStyle}>Height (in)</th>
                <th style={{ ...thStyle, borderRight: 'none', width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pieces.map((piece, idx) => (
                <tr key={piece.id || idx}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #E2E8F0', color: '#5B6B7F', fontSize: '12px', fontWeight: '600' }}>
                    {idx + 1}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input
                      style={inputStyle}
                      type="number"
                      value={piece.weight}
                      onChange={e => updatePiece(piece.id || idx, 'weight', e.target.value)}
                      placeholder="0"
                      autoFocus={idx === pieces.length - 1}
                    />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.length_in} onChange={e => updatePiece(piece.id || idx, 'length_in', e.target.value)} placeholder="0" />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.width_in} onChange={e => updatePiece(piece.id || idx, 'width_in', e.target.value)} placeholder="0" />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                    <input style={inputStyle} type="number" value={piece.height_in} onChange={e => updatePiece(piece.id || idx, 'height_in', e.target.value)} placeholder="0" />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0', textAlign: 'center' }}>
                    <button onClick={() => removePiece(piece.id || idx)} className="fcc-press" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            onClick={addPiece}
            className="fcc-press"
            style={{ marginTop: '10px', background: 'none', border: '1px dashed #F5A800', color: '#F5A800', borderRadius: '6px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', width: '100%' }}
          >
            + Add Piece
          </button>
        </div>

        {/* Totals and preview */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', background: '#F4F7FB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Pieces</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{pieces.length}</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Total Actual Wt</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{totalActual.toFixed(0)} lbs</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Total Dim Wt</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{preview ? `${preview.total_dim_weight} lbs` : '—'}</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Billable Wt</p>
              <p style={{ fontWeight: '600', fontSize: '16px' }}>{preview ? `${preview.billable_weight} lbs` : '—'}</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Liftgate</p>
              <p style={{ fontWeight: '600', fontSize: '16px', color: preview?.liftgate_auto ? '#dc2626' : '#15803D' }}>
                {preview ? (preview.liftgate_auto ? 'YES' : 'No') : '—'}
              </p>
            </div>
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Two-Man</p>
              <p style={{ fontWeight: '600', fontSize: '16px', color: preview?.two_man_auto ? '#dc2626' : '#15803D' }}>
                {preview ? (preview.two_man_auto ? 'YES' : 'No') : '—'}
              </p>
            </div>
            <div style={{ background: '#E9F7EF', border: '1px solid #A7D9BC', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={{ fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' }}>Est. Total</p>
              <p style={{ fontWeight: '700', fontSize: '18px', color: '#15803D' }}>
                {preview ? `$${preview.final_total}` : '...'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
              Cancel
            </button>
            <button
              onClick={() => onConfirm(pieces.map(p => ({
                weight: parseFloat(p.weight) || 0,
                length_in: parseFloat(p.length_in) || 0,
                width_in: parseFloat(p.width_in) || 0,
                height_in: parseFloat(p.height_in) || 0
              })))}
              className="btn btn-primary fcc-press"
              style={{ width: 'auto', padding: '9px 24px' }}
            >
              Confirm Pieces ({pieces.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Delivery Calculator ───────────────────────────────────
const DeliveryCalculator = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [billTo, setBillTo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [rows, setRows] = useState([{ ...emptyRow, id: Date.now() }]);
  const [savedShipments, setSavedShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [pieceModal, setPieceModal] = useState(null);
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem('selected_customer');
    if (!stored) { navigate('/select-customer'); return; }
    setCustomer(JSON.parse(stored));
  }, [navigate]);

  const addRow = () => setRows(prev => [...prev, { ...emptyRow, id: Date.now() }]);
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const openPieceModal = (row) => {
    if (!row.destination_zip || row.destination_zip.length < 5) {
      return toast.error('Enter a valid ZIP code before adding pieces');
    }
    setPieceModal(row);
  };

  const confirmPieces = (rowId, pieces) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, pieces };
      fetchPreviewForRow(updated);
      return updated;
    }));
    setPieceModal(null);
    toast.success(`${pieces.length} piece${pieces.length !== 1 ? 's' : ''} confirmed`);
  };

  const fetchPreviewForRow = async (row) => {
    if (!row.destination_zip || row.destination_zip.length < 5) return;
    if (!row.pieces || row.pieces.length === 0) return;
    const validPieces = row.pieces.filter(p => p.weight > 0);
    if (validPieces.length === 0) return;
    try {
      const res = await API.post('/invoices/preview', {
        customer_id: customer?.customer_id,
        destination_zip: row.destination_zip,
        is_residential: row.is_residential,
        inside_charges: row.inside_charges,
        unpack_debris: row.unpack_debris,
        liftgate_mode: row.liftgate_mode || 'auto',
        special_applied: row.special_applied || false,
        special_time: row.special_time || 'normal',
        two_man_mode: row.two_man_mode || 'auto',
        misc_charges: parseFloat(row.misc_charges) || 0,
        special_charges: parseFloat(row.special_charges) || 0,
        pieces: validPieces
      });
      setPreviews(prev => ({ ...prev, [row.id]: res.data }));
    } catch {
      setPreviews(prev => ({ ...prev, [row.id]: null }));
    }
  };

  const createInvoice = async () => {
    if (!billTo) return toast.error('Bill To is required');
    setLoading(true);
    try {
      const res = await API.post('/invoices', {
        customer_id: customer.customer_id,
        bill_to: billTo,
        invoice_date: invoiceDate
      });
      setInvoice(res.data);
      setInvoiceCreated(true);
      toast.success(`Invoice ${res.data.invoice_number} created`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const saveShipment = async (row, allowDuplicate = false) => {
    if (!invoice) return toast.error('Create the invoice first');
    if (!row.pro_number) return toast.error('Pro number is required');
    if (!row.destination_zip) return toast.error('ZIP code is required');
    if (!row.pieces || row.pieces.length === 0) return toast.error('Add at least one piece first');
    setLoading(true);
    try {
      const res = await API.post(`/invoices/${invoice.invoice_id}/shipments`, {
        ...row,
        allow_duplicate: allowDuplicate,
        misc_charges: parseFloat(row.misc_charges) || 0,
        special_charges: parseFloat(row.special_charges) || 0
      });
      setSavedShipments(prev => [...prev, res.data.shipment]);
      setRows(prev => prev.filter(r => r.id !== row.id));
      setPreviews(prev => { const n = { ...prev }; delete n[row.id]; return n; });
      if (rows.length === 1) addRow();
      toast.success(`Shipment ${row.pro_number} saved — $${res.data.shipment.final_total}`);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.duplicate) {
        setLoading(false);
        if (window.confirm(`Duplicate shipment detected — ${row.pro_number} already exists. Would you like to proceed with entering a duplicate shipment?`)) {
          return saveShipment(row, true);
        }
        return;
      }
      toast.error(err.response?.data?.error || 'Failed to save shipment');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '6px 8px', border: '1px solid #E2E8F0',
    borderRadius: '4px', fontSize: '13px', outline: 'none'
  };

  const thStyle = {
    padding: '10px 8px', background: '#1A2A3A', color: 'white',
    fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
    textAlign: 'left', borderRight: '1px solid #F5A800'
  };

  const thPreviewStyle = { ...thStyle, background: '#3A3C3F' };

  const tdStyle = {
    padding: '6px 8px', borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #f0f0f0', verticalAlign: 'middle'
  };

  const tdPreviewStyle = { ...tdStyle, background: '#FDF4DD', fontSize: '12px' };

  const modeSelectStyle = {
    fontSize: '11px', padding: '2px 4px', border: '1px solid #E8B84B',
    borderRadius: '4px', background: '#FDF4DD', outline: 'none', cursor: 'pointer'
  };

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '100%' }}>

      {/* Piece modal */}
      {pieceModal && (
        <PieceModal
          row={pieceModal}
          customer={customer}
          onConfirm={(pieces) => confirmPieces(pieceModal.id, pieces)}
          onClose={() => setPieceModal(null)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Delivery Calculator</h1>
          {customer && <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Customer: <strong>{customer.name}</strong></p>}
        </div>
        <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
          ← Dashboard
        </button>
      </div>

      {/* Invoice header */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Invoice Details</h2>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Bill To</label>
            <input style={{ ...inputStyle, width: '220px' }} value={billTo} onChange={e => setBillTo(e.target.value)} placeholder="Customer name" disabled={invoiceCreated} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Invoice Date</label>
            <input type="date" style={{ ...inputStyle, width: '160px' }} value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} disabled={invoiceCreated} />
          </div>
          {!invoiceCreated ? (
            <button className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 20px' }} onClick={createInvoice} disabled={loading}>
              Create Invoice
            </button>
          ) : (
            <div style={{ background: '#E9F7EF', border: '1px solid #A7D9BC', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', color: '#15803D', fontWeight: '500' }}>
              ✓ {invoice?.invoice_number}
            </div>
          )}
        </div>
      </div>

      {/* Shipment entry table */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Enter Shipments</h2>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginTop: '2px' }}>Enter ZIP and Pro #, then click "Pieces" to add piece details</p>
          </div>
          <button onClick={addRow} className="fcc-press" style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}>
            + Add Row
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Pro #</th>
                <th style={thStyle}>ZIP</th>
                <th style={thStyle}>Pieces</th>
                <th style={thStyle}>Res</th>
                <th style={thStyle}>P/D</th>
                <th style={thStyle}>Inside</th>
                <th style={thStyle}>Unpack</th>
                <th style={thStyle}>Misc $</th>
                <th style={thStyle}>Special</th>
                <th style={thStyle}>Notes</th>
                <th style={thPreviewStyle}>Zone</th>
                <th style={thPreviewStyle}>Act. Wt</th>
                <th style={thPreviewStyle}>Bill. Wt</th>
                <th style={thPreviewStyle}>Lift</th>
                <th style={thPreviewStyle}>2-Man</th>
                <th style={{ ...thPreviewStyle, borderRight: '2px solid #F5A800' }}>Est. Total</th>
                <th style={{ ...thStyle, borderRight: 'none' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const p = previews[row.id];
                const pieceCount = row.pieces?.length || 0;
                return (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      <input style={inputStyle} value={row.pro_number} onChange={e => updateRow(row.id, 'pro_number', e.target.value)} placeholder="PRO-001" />
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, width: '70px' }} value={row.destination_zip} onChange={e => updateRow(row.id, 'destination_zip', e.target.value)} placeholder="33508" maxLength={5} />
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => openPieceModal(row)}
                        className="fcc-press"
                        style={{
                          background: pieceCount > 0 ? '#E9F7EF' : '#FDF4DD',
                          border: `1px solid ${pieceCount > 0 ? '#A7D9BC' : '#F2D89A'}`,
                          color: pieceCount > 0 ? '#15803D' : '#F5A800',
                          borderRadius: '6px', padding: '5px 10px',
                          cursor: 'pointer', fontSize: '12px', fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {pieceCount > 0 ? `✓ ${pieceCount} pc${pieceCount !== 1 ? 's' : ''}` : '+ Pieces'}
                      </button>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={row.is_residential} onChange={e => updateRow(row.id, 'is_residential', e.target.checked)} />
                    </td>
                    <td style={tdStyle}>
                      <select style={{ ...inputStyle, width: '55px' }} value={row.pickup_or_delivery} onChange={e => updateRow(row.id, 'pickup_or_delivery', e.target.value)}>
                        <option value="D">D</option>
                        <option value="P">P</option>
                      </select>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={row.inside_charges} onChange={e => updateRow(row.id, 'inside_charges', e.target.checked)} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={row.unpack_debris} onChange={e => updateRow(row.id, 'unpack_debris', e.target.checked)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, width: '65px' }} type="number" value={row.misc_charges} onChange={e => updateRow(row.id, 'misc_charges', e.target.value)} placeholder="0.00" />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={!!row.special_applied}
                            onChange={e => { updateRow(row.id, 'special_applied', e.target.checked); fetchPreviewForRow({ ...row, special_applied: e.target.checked }); }}
                          />
                          Special
                        </label>
                        {row.special_applied && p && ['A', 'B', 'C', 'D'].includes(p.zone) && (
                          <select
                            style={{ ...inputStyle, width: '120px' }}
                            value={row.special_time || 'normal'}
                            onChange={e => { updateRow(row.id, 'special_time', e.target.value); fetchPreviewForRow({ ...row, special_time: e.target.value }); }}
                          >
                            <option value="normal">Normal Hours</option>
                            <option value="after_hours">After Hours</option>
                            <option value="weekend">Weekend/Holiday</option>
                          </select>
                        )}
                        {row.special_applied && p && !['A', 'B', 'C', 'D'].includes(p.zone) && (
                          <span style={{ fontSize: '11px', color: '#5B6B7F', whiteSpace: 'nowrap' }}>
                            {p.approx_miles} mi × rate
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, width: '120px' }} value={row.misc_notes} onChange={e => updateRow(row.id, 'misc_notes', e.target.value)} placeholder="Notes..." />
                    </td>
                    <td style={tdPreviewStyle}>{p ? p.zone : '—'}</td>
                    <td style={tdPreviewStyle}>{p ? `${p.total_actual_weight} lbs` : '—'}</td>
                    <td style={tdPreviewStyle}>{p ? `${p.billable_weight} lbs` : '—'}</td>
                    <td style={tdPreviewStyle}>
                      {p ? (
                        <select
                          value={row.liftgate_mode || 'auto'}
                          onChange={e => { updateRow(row.id, 'liftgate_mode', e.target.value); fetchPreviewForRow({ ...row, liftgate_mode: e.target.value }); }}
                          style={modeSelectStyle}
                        >
                          <option value="auto">Auto {p.liftgate_auto ? '(on)' : '(off)'}</option>
                          <option value="waived">Waived</option>
                          <option value="applied">Applied</option>
                        </select>
                      ) : '—'}
                    </td>
                    <td style={tdPreviewStyle}>
                      {p ? (
                        <select
                          value={row.two_man_mode || 'auto'}
                          onChange={e => { updateRow(row.id, 'two_man_mode', e.target.value); fetchPreviewForRow({ ...row, two_man_mode: e.target.value }); }}
                          style={modeSelectStyle}
                        >
                          <option value="auto">Auto {p.two_man_auto ? '(on)' : '(off)'}</option>
                          <option value="waived">Waived</option>
                          <option value="applied">Applied</option>
                        </select>
                      ) : '—'}
                    </td>
                    <td style={{ ...tdPreviewStyle, fontWeight: '600', color: p ? '#15803D' : '#8595A6', borderRight: '2px solid #F5A800' }}>
                      {p ? `$${p.final_total}` : '...'}
                    </td>
                    <td style={{ ...tdStyle, borderRight: 'none', whiteSpace: 'nowrap' }}>
                      <button onClick={() => saveShipment(row)} disabled={loading} className="fcc-press" style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>
                        Save
                      </button>
                      <button onClick={() => removeRow(row.id)} className="fcc-press" style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 8px', cursor: 'pointer', fontSize: '12px' }}>
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Saved shipments */}
      {savedShipments.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Saved Shipments ({savedShipments.length})</h2>
            <button onClick={() => navigate(`/invoices/${invoice.invoice_id}`)} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '6px 16px', fontSize: '13px' }}>
              View Invoice →
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {['Pro #', 'Zone', 'Pcs', 'Act. Wt', 'Bill. Wt', 'P/D', 'Base', 'FSC', 'Liftgate', 'Res', '2-Man', 'Inside', 'Unpack', 'Misc', 'Special', 'Total', 'Notes'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {savedShipments.map(s => (
                  <tr key={s.shipment_id}>
                    <td style={tdStyle}>{s.pro_number}</td>
                    <td style={tdStyle}>{s.zone}</td>
                    <td style={tdStyle}>{s.pieces?.length || '—'}</td>
                    <td style={tdStyle}>{s.total_actual_weight} lbs</td>
                    <td style={tdStyle}>{s.billable_weight} lbs</td>
                    <td style={tdStyle}>{s.pickup_or_delivery}</td>
                    <td style={tdStyle}>${s.base_charge}</td>
                    <td style={tdStyle}>${s.fuel_surcharge}</td>
                    <td style={tdStyle}>${s.liftgate_fee}</td>
                    <td style={tdStyle}>${s.residential_charge}</td>
                    <td style={tdStyle}>${s.two_man_charge}</td>
                    <td style={tdStyle}>${s.inside_charge_amt}</td>
                    <td style={tdStyle}>${s.unpack_charge_amt}</td>
                    <td style={tdStyle}>${s.misc_charges}</td>
                    <td style={tdStyle}>${s.special_charges}</td>
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#15803D' }}>${s.final_total}</td>
                    <td style={{ ...tdStyle, color: '#5B6B7F', whiteSpace: 'normal', minWidth: '140px' }}>
                      {[s.misc_notes, s.special_notes].map(x => (x ? String(x).trim() : '')).filter(Boolean).join(' — ') || '—'}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#F4F7FB' }}>
                  <td colSpan={15} style={{ ...tdStyle, fontWeight: '600', textAlign: 'right' }}>Invoice Total:</td>
                  <td style={{ ...tdStyle, fontWeight: '700', color: '#15803D', fontSize: '14px' }}>
                    ${savedShipments.reduce((sum, s) => sum + parseFloat(s.final_total), 0).toFixed(2)}
                  </td>
                  <td style={tdStyle}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryCalculator;
